import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    DEFAULT_PERIODS_PER_DAY, DEFAULT_TIMEZONE, HOURS_PER_DAY, MINUTES_PER_HOUR,
} from '@/constants/Time.ts';

dayjs.extend(utc);
dayjs.extend(timezone);

export function getNextPeriodBoundary (time: Dayjs, useTradingPeriod: boolean = false): Dayjs {
    const hour = time.startOf('hour');
    const minute = time.minute();

    if (!useTradingPeriod) {
        return minute < 30 ? hour.minute(30) : hour.add(1, 'hour');
    }
    if (minute < 15) return hour.minute(15);
    if (minute < 45) return hour.minute(45);
    return hour.add(1, 'hour').minute(15);
}

export function getDatePeriods (date: string | Date | Dayjs, timezoneId: string = DEFAULT_TIMEZONE, periodsPerDay: number = DEFAULT_PERIODS_PER_DAY): number {
    const initialDate = dayjs(date).startOf('day');
    const currentOffset = initialDate.tz(timezoneId).utcOffset();
    const nextOffset = initialDate.add(1, 'day').tz(timezoneId).utcOffset();
    const periodsPerHour = periodsPerDay / HOURS_PER_DAY;

    if (nextOffset !== currentOffset) {
        return nextOffset < currentOffset ? periodsPerDay + periodsPerHour : periodsPerDay - periodsPerHour;
    }

    return periodsPerDay;
}

export function getPeriodFromTime (time?: Dayjs | Date | string | number, periodsPerDay: number = DEFAULT_PERIODS_PER_DAY, timezoneId: string = DEFAULT_TIMEZONE): number {
    const givenDate = dayjs(time).tz(timezoneId);
    const startOfDay = givenDate.startOf('day');
    const endOfDay = startOfDay.add(1, 'day');
    const periods = getDatePeriods(givenDate, timezoneId, periodsPerDay);
    const totalMinutesInDay = endOfDay.diff(startOfDay, 'minute');

    const minutesElapsed = givenDate.diff(startOfDay, 'minute');
    const rawPeriod = Math.floor((minutesElapsed / totalMinutesInDay) * periods) + 1;
    return Math.min(Math.max(rawPeriod, 1), periods);
}

export function getTimeFromPeriod (
    halfHourlyPeriodIndex: number,
    date: string | Date | Dayjs,
    periodsPerDayAssumedForPeriod: number = DEFAULT_PERIODS_PER_DAY,
    timezoneId: string = DEFAULT_TIMEZONE,
): Dayjs {
    const periodIndexAdjustment = periodsPerDayAssumedForPeriod / DEFAULT_PERIODS_PER_DAY;
    const period = halfHourlyPeriodIndex * periodIndexAdjustment;
    const givenDate = dayjs(date).tz(timezoneId).startOf('day');
    const periods = getDatePeriods(givenDate, timezoneId, periodsPerDayAssumedForPeriod);

    if (period < 1 || period > periods) return dayjs();

    const minutesPerPeriod = (HOURS_PER_DAY * MINUTES_PER_HOUR) / periodsPerDayAssumedForPeriod;
    return givenDate.add(Math.floor(period - periodIndexAdjustment) * minutesPerPeriod, 'minute');
}
